'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'

import JoinArenaArrow from '@/assets/icons/join-arena-arrow.svg'
import { getClubDashboardHref } from '@/features/clubs/getClubDashboardHref'
import { useClubMe } from '@/hooks/useClubMe'
import { useForClubsAudience } from '@/hooks/useForClubsAudience'
import { Link } from '@/i18n/routing'
import { useAuth } from '@/providers/AuthProvider'

import {
  CLUBS_EMAIL,
  CLUBS_SUPPORT_EMAIL,
  STADIUM_FANS_IMG
} from '../forClubsLandingContent.constants'
import S from './ForClubsCta.module.scss'

const ForClubsCta = () => {
  const { openAuthModal } = useAuth()
  const t = useTranslations('forClubsPage.members.cta')
  const { showClubOwnerExperience, isGuest } = useForClubsAudience()
  const { data: clubData } = useClubMe({ enabled: showClubOwnerExperience })
  const dashboardHref = getClubDashboardHref(clubData)

  const benefits = showClubOwnerExperience
    ? [t('authBenefit1'), t('authBenefit2'), t('authBenefit3')]
    : [t('benefit1'), t('benefit2'), t('benefit3')]

  return (
    <div className={S.stadiumFansBand}>
      <div className={S.stadiumFansBandInner}>
        <Image
          src={STADIUM_FANS_IMG}
          alt=""
          fill
          sizes="100vw"
          quality={60}
          className={S.stadiumFansImage}
        />
        <div className={S.stadiumFansDarken} aria-hidden />
        <div className={S.stadiumFansGrain} aria-hidden />
        <div className={S.stadiumFansTopBlend} aria-hidden />
        <div className={S.stadiumFansBottomBlend} aria-hidden />
        <div className={S.stadiumFansForeground}>
          <div className={S.stadiumFansCopy}>
            <div className={S.stadiumFansBenefits}>
              {benefits.map((benefit, index) => (
                <div key={benefit} className={S.stadiumFansBenefitItem}>
                  <span className={S.stadiumFansBenefitText}>{benefit}</span>
                  {index < benefits.length - 1 && (
                    <span className={S.stadiumFansBenefitDot} aria-hidden />
                  )}
                </div>
              ))}
            </div>
            <p className={S.stadiumFansTitle}>
              {showClubOwnerExperience ? t('authTitle') : t('title')}
            </p>
            <div className={S.stadiumFansBody}>
              <div className={S.stadiumFansBodyDesktop}>
                <p className={S.stadiumFansBodyLine}>
                  {showClubOwnerExperience ? t('authBody') : t('body')}
                </p>
              </div>
            </div>
          </div>
          <div className={S.stadiumFansActions}>
            {showClubOwnerExperience ? (
              <Link href={dashboardHref} className={S.stadiumFansPrimaryBtn}>
                <span className={S.stadiumFansPrimaryBtnText}>
                  {t('viewDashboard')}
                </span>
                <JoinArenaArrow className={S.stadiumFansPrimaryBtnIcon} />
              </Link>
            ) : (
              isGuest && (
                <button
                  type="button"
                  className={S.stadiumFansPrimaryBtn}
                  onClick={() =>
                    openAuthModal('emailSignupEntityChoose', 'landing')
                  }
                >
                  <span className={S.stadiumFansPrimaryBtnText}>
                    {t('joinNow')}
                  </span>
                  <JoinArenaArrow className={S.stadiumFansPrimaryBtnIcon} />
                </button>
              )
            )}
            <a
              href={`mailto:${CLUBS_EMAIL}`}
              className={S.stadiumFansSecondaryBtn}
            >
              <span className={S.stadiumFansSecondaryBtnText}>
                {t('contactUs')}
              </span>
            </a>
          </div>
          <p className={S.stadiumFansContactText}>
            {showClubOwnerExperience ? (
              <>
                {t('helpText')}{' '}
                <a
                  href={`mailto:${CLUBS_SUPPORT_EMAIL}`}
                  className={S.stadiumFansContactLink}
                >
                  {CLUBS_SUPPORT_EMAIL}
                </a>
              </>
            ) : (
              <>
                {t('questionsText')}{' '}
                <a
                  href={`mailto:${CLUBS_EMAIL}`}
                  className={S.stadiumFansContactLink}
                >
                  {CLUBS_EMAIL}
                </a>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

export default ForClubsCta
